import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  useMedia,
  useMobileSimplifiedToothChartVisibility,
} from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { MobileLayout } from '@/shared/ui';

import { ReportLayout, ReportSettingsToggle, i18n } from '@/entities/reports';

import { MedicalImageToolbar } from '@/features/medicalImageToolbar';
import { MaskFiltersWrapper, MaskTooltip } from '@/features/renderMasks';
import { useReportCheck } from '@/features/reportCheck';
import { SimplifiedToothChart } from '@/features/simplifiedToothChart';

import { Conclusion } from '@/widgets/Conclusion';
import { MobileHeader } from '@/widgets/Header';
import { PanowingsReportRender } from '@/widgets/PanowingsReportRender';
import { Report2DToothChart } from '@/widgets/Report2DToothChart';
import { ReportActions } from '@/widgets/ReportActions';
import { ToothCardList } from '@/widgets/ToothCard';

import { ToothNumberEditPopup } from '@/pages/GpReport/ui/ToothNumberEditPopup';

import { PanowingChangeTeethNumberModal } from '../PanowingChangeTeethNumberModal/PanowingChangeTeethNumberModal';
import { PanowingImageSlider } from '../PanowingImageSlider/PanowingImageSlider';

import styles from './PanowignsReport.module.scss';

export const PanowingsReport = () => {
  const { patientID, reportID = '' } = useParams();
  const { formatMessage } = useIntl();
  const { isMobile } = useMedia();

  const mobileToothChartStyle = styles.mobileToothChart;

  const { simplifiedChartRef, shouldShowMobileSimplifiedToothChart } =
    useMobileSimplifiedToothChartVisibility({
      mobileToothChartStyle,
    });

  useReportCheck(reportID);

  useEffect(() => {
    trackEvent({
      eventName: 'PanowingsReport Page Viewed',
      properties: {
        patient_id: patientID,
        report_id: reportID,
        report_type: ReportType.ReportType_Pano_Bitewings,
      },
      isPageViewEvent: true,
    });
  }, []);

  if (isMobile) {
    return (
      <MobileLayout>
        <MobileHeader />

        {shouldShowMobileSimplifiedToothChart && (
          <div
            ref={simplifiedChartRef}
            className={styles.simplifiedToothChartWrapper}
          >
            <SimplifiedToothChart
              reportID={reportID}
              patientID={patientID ?? ''}
              useScroll
              mergePrimaryWithAdult
            />
          </div>
        )}
        <MobileLayout.Main>
          <PanowingsReportRender>
            <MaskFiltersWrapper className={styles.maskFilters} />
            <MaskTooltip />
            <MedicalImageToolbar
              toolbarID="main"
              className={styles.mobileToolbar}
            />
            <PanowingImageSlider />
          </PanowingsReportRender>

          <ReportSettingsToggle />

          <Report2DToothChart className={mobileToothChartStyle} />
          <ToothCardList />
          <Conclusion />
        </MobileLayout.Main>
        <MobileLayout.Footer>
          <ReportActions
            patientID={patientID as string}
            reportID={reportID as string}
          />
        </MobileLayout.Footer>

        {/* Modals */}
        <PanowingChangeTeethNumberModal />
      </MobileLayout>
    );
  }

  return (
    <>
      <ReportLayout.LeftColumn data-section="panowings-report">
        <h1>{formatMessage(i18n[ReportType.ReportType_Pano_Bitewings])}</h1>
        <PanowingsReportRender>
          <MaskFiltersWrapper className={styles.maskFilters} />
          <MaskTooltip />
          <MedicalImageToolbar toolbarID="main" />
          <PanowingImageSlider />
          <ToothNumberEditPopup />
        </PanowingsReportRender>
      </ReportLayout.LeftColumn>
      <ReportLayout.RightColumn data-section="panowings-report">
        <Report2DToothChart />

        <ToothCardList />

        <Conclusion />

        <ReportActions
          patientID={patientID as string}
          reportID={reportID as string}
        />
      </ReportLayout.RightColumn>
      {/* Modals */}
    </>
  );
};
